import { Component, Input, OnChanges, OnInit, ViewChild, inject } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { IIndicator } from 'src/app/_models/indicator.vm';
import { IPeriod } from 'src/app/_models/period.vm';
import { ISystemLevel } from 'src/app/_models/systemLevel.vm';
import { ExportTableService, ExportType } from 'src/app/_services/exportTable.service';
import { environment } from 'src/environments/environment';
import { trackByIndex, trackByValue } from '../utils/track-by';
import { orderNumeratorFirst } from '../utils/table-columns';
import { ErrorReportingService } from '../../_services/error-reporting.service';

/** Keeps the search field's id unique when several tables are compared on one page. */
let extractTableCount = 0;

@Component({
    selector: 'app-extract-table',
    templateUrl: './extract-table.component.html',
    styleUrls: ['./extract-table.component.scss'],
    standalone: false
})
export class ExtractTableComponent implements OnInit, OnChanges {
  private readonly errorReporting = inject(ErrorReportingService);

  public readonly trackByIndex = trackByIndex;
  public readonly trackByValue = trackByValue;
  public readonly optionSearchId: string = `xtOptionSearch${++extractTableCount}`;

  @Input() public indicatorData: any
  @Input() public indicatorSelected: IIndicator
  @Input() public periodSelected: IPeriod
  @Input() public systemLevelSelected: ISystemLevel
  @Input() public itemsPerPage: number
  @Input() public currentPage: number
  @Input() public tableHeaders: Array<string>
  @Input() public tableInfo: string
  @Input() public tableFilters: string[]

  // Closed from code once a value is picked. The menu is left open on an outside
  // click of its own so moving between the two columns does not dismiss it.
  @ViewChild('filterDropdown') private filterDropdown: NgbDropdown;

  public exportLink: string = environment.apiUrl;

  // Paging -------------------------------------------------------------------
  // itemsPerPage is the starting page size the page asks for; rowsPerPage is what
  // the reader has since chosen, which is theirs to keep as the data changes
  // underneath it.
  public rowsPerPage: number;
  public readonly rowsPerPageOptions: ReadonlyArray<number> = [20, 50, 100, 200];
  /** Rows skipped before the page on screen. Feeds the slice in the template. */
  public pageOffset: number = 0;
  public isFilterGroupOpened: boolean = false;
  public selectedFilterGroup : string
  public selectedFilterGroups: string[] = [];
  public filterGroupOptions: string[] = [];
  /** Narrows a long list of values, such as the age groups or the ethnicity split. */
  public optionSearch: string = '';
  public filteredIndicatorData: any
  public originalIndicatorData: any
  public selectedOptions: { selectedFilterGroup: string, selectedOptions: string[] }[] = [];

  // Columns ----------------------------------------------------------------
  // An extract is nearly twenty columns wide, and several of them carry the same
  // value on every row or nothing at all. Showing them all meant most of the table
  // sat off the side of the screen, so the ones that do not vary start out folded
  // away and can be brought back from the Columns menu.

  /**
   * Every column the extract offers, in the order they are presented in. That is
   * the order the API supplies, except that Numerator is moved ahead of
   * Denominator to match how the two read everywhere else in the tool.
   */
  public orderedHeaders: Array<string> = [];
  /** The columns the table is drawing, a subset of the above in the same order. */
  public visibleHeaders: Array<string> = [];
  /** Same value on every row. Stated once above the table instead. */
  public constantColumns: Array<{ header: string, value: string }> = [];
  /** Nothing in them on any row. */
  public emptyColumns: Array<string> = [];
  /** Right aligned, and never wrapped. */
  public numericColumns: Array<string> = [];
  /** True once the user has asked for every column, which also unfolds the panel above. */
  public showAllColumnsOn: boolean = false;

  /** The header set the analysis above was run for, so filtering does not redo it. */
  private analysedFor: string = null;

  constructor(private exportTableService: ExportTableService ) { }

  ngOnInit() {
  }

  ngOnChanges() {
    this.originalIndicatorData = this.indicatorData
    // Only on the first pass: re-seeding on every change would undo the reader's
    // own choice each time the extract is redrawn.
    if (!this.rowsPerPage) {
      this.rowsPerPage = this.itemsPerPage || this.rowsPerPageOptions[0];
    }
    this.resetPaging();
    this.analyseColumns();
  }

  // Columns ------------------------------------------------------------------

  private analyseColumns(): void {
    const headers = orderNumeratorFirst(this.tableHeaders || []);
    const rows: Array<any> = this.originalIndicatorData || [];
    const signature = headers.join('|');
    // Only the extract changing warrants a fresh analysis. Re-running it whenever
    // the table filters would throw away the user's own column choices.
    if (signature === this.analysedFor) {
      return;
    }
    this.analysedFor = signature;
    this.orderedHeaders = headers;

    this.constantColumns = [];
    this.emptyColumns = [];
    this.numericColumns = [];

    headers.forEach(header => {
      let first: string = null;
      let constant = true;
      let empty = true;
      let sample: string = null;

      // Practice level extracts run to tens of thousands of rows, so each column
      // stops being read the moment there is nothing left for it to learn.
      for (const row of rows) {
        const value = row[header] === null || row[header] === undefined ? '' : String(row[header]);
        if (value !== '') {
          empty = false;
          if (sample === null) {
            sample = value;
          }
        }
        if (first === null) {
          first = value;
        } else if (value !== first) {
          constant = false;
        }
        if (!constant && !empty && sample !== null) {
          break;
        }
      }

      if (empty) {
        this.emptyColumns.push(header);
      } else if (constant && rows.length > 1) {
        this.constantColumns.push({ header, value: first });
      }
      if (sample !== null && /^-?[\d,]+(\.\d+)?$/.test(sample.trim())) {
        this.numericColumns.push(header);
      }
    });

    this.resetColumns();
  }

  public resetColumns(): void {
    const headers = this.orderedHeaders;
    this.showAllColumnsOn = false;
    this.visibleHeaders = headers.filter(header => !this.isEmptyColumn(header) && !this.isConstantColumn(header));
    // Every column is the same on every row, which happens on a single row extract.
    // Something has to be on screen, so nothing is folded away.
    if (!this.visibleHeaders.length) {
      this.showAllColumns();
    }
  }

  public showAllColumns(): void {
    this.visibleHeaders = [...this.orderedHeaders];
    this.showAllColumnsOn = true;
  }

  public toggleColumn(header: string): void {
    if (this.isColumnVisible(header)) {
      // The last column cannot be turned off: an empty table says nothing.
      if (this.visibleHeaders.length === 1) {
        return;
      }
      this.visibleHeaders = this.visibleHeaders.filter(item => item !== header);
    } else {
      // Put back where the extract has it, not on the end.
      this.visibleHeaders = this.orderedHeaders.filter(item => item === header || this.visibleHeaders.includes(item));
    }
    this.showAllColumnsOn = this.visibleHeaders.length === this.orderedHeaders.length;
  }

  public isColumnVisible(header: string): boolean {
    return this.visibleHeaders.includes(header);
  }

  public isConstantColumn(header: string): boolean {
    return this.constantColumns.some(column => column.header === header);
  }

  public isEmptyColumn(header: string): boolean {
    return this.emptyColumns.includes(header);
  }

  public isNumericColumn(header: string): boolean {
    return this.numericColumns.includes(header);
  }

  onPageChange(pageNumber: number): void {
    this.pageOffset = this.rowsPerPage * (pageNumber - 1);
  }

  public setRowsPerPage(rows: number): void {
    this.rowsPerPage = rows;
    // The row that was at the top is no longer at the top of any page, so the only
    // honest place to land is the beginning.
    this.resetPaging();
  }

  /**
   * Back to the first page. Called whenever the rows underneath change, since a
   * filter that cuts the extract to 30 rows would otherwise leave the reader on
   * page 12 looking at nothing.
   */
  private resetPaging(): void {
    this.currentPage = 1;
    this.pageOffset = 0;
  }

  public get totalRows(): number {
    return this.indicatorData ? this.indicatorData.length : 0;
  }

  public get rangeStart(): number {
    return this.totalRows ? this.pageOffset + 1 : 0;
  }

  public get rangeEnd(): number {
    return Math.min(this.pageOffset + this.rowsPerPage, this.totalRows);
  }

  // Filtering ----------------------------------------------------------------

  /** Both columns start over each time the menu is opened. */
  public onFilterOpenChange(open: boolean): void {
    if (open && !this.selectedFilterGroup && this.tableFilters?.length) {
      // Straight to the values of the first field, rather than an empty right column.
      this.showFilterGroupOptions(this.tableFilters[0]);
    }
  }

  // The highlight is a class binding rather than a DOM edit: a document-wide
  // querySelectorAll would reach into the other tables when several extracts are
  // being compared on one page.
  showFilterGroupOptions(filter: string): void {
    this.selectedFilterGroup = filter
    this.isFilterGroupOpened = true
    this.optionSearch = ''
    this.filterGroupOptions = []
    this.originalIndicatorData.forEach(e => {
      if (!this.filterGroupOptions.includes(e[filter])) {
        this.filterGroupOptions.push(e[filter])
      }
    });
  }

  /** Back to the field list on narrow viewports, where only one column is on screen. */
  public backToFilterFields(): void {
    this.isFilterGroupOpened = false;
  }

  public onOptionSearchChange(term: string): void {
    this.optionSearch = term;
  }

  public get visibleFilterOptions(): Array<string> {
    const term = this.optionSearch.trim().toLowerCase();
    if (!term) {
      return this.filterGroupOptions;
    }
    return this.filterGroupOptions.filter(option => (option || '').toLowerCase().includes(term));
  }

  /** Whether the value list is long enough to be worth searching. */
  public get showOptionSearch(): boolean {
    return this.isFilterGroupOpened && this.filterGroupOptions.length > 8;
  }

  public get activeFilterLabel(): string {
    const active = this.selectedOptions[0];
    return active ? `${active.selectedFilterGroup}: ${active.selectedOptions[0]}` : null;
  }

  onFilterGroupOptionSelect(option: string): void {
    const optionObj = { selectedFilterGroup: this.selectedFilterGroup, selectedOptions: [option] }  
    if (this.selectedOptions.some(item => JSON.stringify(item) === JSON.stringify(optionObj))) {
      this.selectedOptions = []
      this.selectedFilterGroups = []
      this.filteredIndicatorData = this.originalIndicatorData
      
    } else {
      this.selectedOptions = []
      this.selectedOptions.push(optionObj)
      this.selectedFilterGroups = []
      this.selectedFilterGroups.push(this.selectedFilterGroup)
      this.filteredIndicatorData = this.originalIndicatorData.filter(i => {     
        return i[this.selectedFilterGroup] == option
      })
    }
    this.indicatorData = this.filteredIndicatorData
    this.resetPaging();
    this.filterDropdown?.close();
  }

  removeFilterOptions(): void {
    this.selectedOptions = [];
    this.selectedFilterGroups = []
    this.indicatorData = this.originalIndicatorData
    this.resetPaging();
  }

  tickOptionCheckBox(option: string): boolean {
    return this.selectedOptions.some(o => {
      return o.selectedFilterGroup === this.selectedFilterGroup && o.selectedOptions.includes(option);
    });
  }

  async exportTable(fileExtension: string) {
    // Every column, whatever the table is showing, in the order the table presents
    // them: a download of an extract is the extract in full.
    const keys = orderNumeratorFirst(Object.keys(this.indicatorData[0]));
    const columns = keys.map(key => ({ name: key, field: key }));
    // Assign file name
    const fileName = `cvd_export_[${this.indicatorSelected.IndicatorShortName}]`
    // Define type
    let type: ExportType
    if (fileExtension == 'CSV') {
      type = ExportType.CSV
    }
    if (fileExtension == 'XLSX') {
      type = ExportType.XLSX 
    }
    try {
      await this.exportTableService.exportDataToCSVOrXLSX(this.indicatorData, columns, fileName, type, 'extract table');

    } catch (error) {
      this.errorReporting.report('Exporting the extract table', error);
    }
  }

}
